import { useEffect, type ReactNode } from 'react'
import { useAuth } from 'react-oidc-context'
import { hasRequiredRole, REQUIRED_ROLE } from '@/auth/oidcConfig'
import { StatusScreen } from '@/components/StatusScreen'

interface RequireAuthProps {
  readonly children: ReactNode
}

/**
 * Schützt den gesamten Anwendungsbereich.
 *
 * Statt einer stillen Weiterleitung zeigt diese Komponente in jedem
 * Zwischenzustand, was gerade passiert. Wer ohne Erklärung auf einer
 * weißen Seite landet, meldet einen Fehler, den es gar nicht gibt.
 */
export function RequireAuth({ children }: RequireAuthProps): ReactNode {
  const auth = useAuth()

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.error && !auth.activeNavigator) {
      void auth.signinRedirect()
    }
  }, [auth])

  if (auth.activeNavigator === 'signinSilent') {
    return <StatusScreen title="Sitzung wird erneuert" tone="info" />
  }

  if (auth.activeNavigator === 'signoutRedirect') {
    return <StatusScreen title="Abmeldung läuft" tone="info" />
  }

  if (auth.isLoading) {
    return <StatusScreen title="Anmeldung wird geprüft" tone="info" />
  }

  if (auth.error) {
    return (
      <StatusScreen
        title="Anmeldung fehlgeschlagen"
        tone="error"
        detail={auth.error.message}
        hint="Häufigste Ursache: Die Redirect-URI dieses Frontends ist in Keycloak nicht hinterlegt. Siehe README, Abschnitt 'Was noch fehlt'."
        action={{ label: 'Erneut versuchen', onClick: () => void auth.signinRedirect() }}
      />
    )
  }

  if (!auth.isAuthenticated) {
    return <StatusScreen title="Weiterleitung zur Anmeldung" tone="info" />
  }

  if (!hasRequiredRole(auth.user)) {
    return (
      <StatusScreen
        title="Keine Berechtigung"
        tone="error"
        detail={`Deinem Konto fehlt die Rolle "${REQUIRED_ROLE}".`}
        hint="Die Anmeldung hat geklappt, aber die Weltmaschine verlangt diese Rolle für jeden API-Aufruf. Ohne sie liefert jede Anfrage 403. Die Rolle muss in Keycloak zugewiesen werden."
        action={{ label: 'Abmelden', onClick: () => void auth.signoutRedirect() }}
      />
    )
  }

  return children
}
